import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Send, CheckCircle, User, Building, Info } from "lucide-react";
import Footer from "../../Components/Footer";
import { useLanguage } from "../../contexts/LanguageContext";

export default function ContactsSection({ onNavigate, onOpenPrivacyPolicy, onOpenCookiePolicy, onOpenTermsOfService }) {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    azienda: '',
    nome_cognome: '',
    professione: '',
    email: '',
    telefono: '',
    indirizzo: '',
    citta: '',
    cap: '',
    provincia: '',
    nazione: 'Italia',
    tipo_richiesta: '',
    messaggio: '',
    privacy: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const teamMembers = [
    {
      name: "Claudio Lazzaroni",
      role: "Management and Coordination",
      description: t.contacts.teamClaudioDesc,
      phone: "+39 348 4412235",
    },
    {
      name: "Stefano Biestro",
      role: "Area Manager",
      description: t.contacts.teamStefanoDesc,
      phone: "+39 348 4412237",
    },
    {
      name: "Achille Valeri",
      role: "Consulente Tecnico Commerciale",
      description: t.contacts.teamAchilleDesc,
      phone: "+39 393 6921248",
    },
    {
      name: "Gabriele Capoferri",
      role: "Consulente Tecnico Commerciale",
      description: t.contacts.teamGabrieleDesc,
      phone: "+39 335 5755767",
    },
    {
      name: "Silvia Lazzaroni",
      role: "Marketing & Comunicazione",
      description: t.contacts.teamSilviaDesc,
      phone: "+39 392 9018126",
    },
    {
      name: "Maria Teresa Cortiana",
      role: "Amministrazione",
      description: t.contacts.teamMariaTeresaDesc,
      phone: "+39 02 2895741",
    },
    {
      name: "Stefania Morlacchi",
      role: "Customer Service",
      description: t.contacts.teamStefaniaDesc,
      phone: "+39 0331 722875",
      mobile: "+39 392 9018126",
    }
  ];

  const containerAnimation = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemAnimation = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const professioni = [
    t.contacts.prof1, t.contacts.prof2, t.contacts.prof3, t.contacts.prof4,
    t.contacts.prof5, t.contacts.prof6, t.contacts.prof7, t.contacts.prof8,
    t.contacts.prof9, t.contacts.prof10, t.contacts.prof11
  ];

  const nazioni = [
    "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua e Barbuda", "Arabia Saudita", "Argentina", "Armenia", "Australia", "Austria", "Azerbaigian", "Bahamas", "Bahrein", "Bangladesh", "Barbados", "Belgio", "Belize", "Benin", "Bhutan", "Bielorussia", "Birmania (Myanmar)", "Bolivia", "Bosnia ed Erzegovina", "Botswana", "Brasile", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cambogia", "Camerun", "Canada", "Capo Verde", "Ciad", "Cile", "Cina", "Cipro", "Colombia", "Comore", "Congo", "Corea del Nord", "Corea del Sud", "Costa d'Avorio", "Costa Rica", "Croazia", "Cuba", "Danimarca", "Dominica", "Ecuador", "Egitto", "El Salvador", "Emirati Arabi Uniti", "Eritrea", "Estonia", "Eswatini", "Etiopia", "Figi", "Filippine", "Finlandia", "Francia", "Gabon", "Gambia", "Georgia", "Germania", "Ghana", "Giamaica", "Giappone", "Gibuti", "Giordania", "Grecia", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guinea Equatoriale", "Guyana", "Haiti", "Honduras", "India", "Indonesia", "Iran", "Iraq", "Irlanda", "Islanda", "Isole Marshall", "Isole Salomone", "Israele", "Italia", "Kazakistan", "Kenya", "Kirghizistan", "Kiribati", "Kosovo", "Kuwait", "Laos", "Lesotho", "Lettonia", "Libano", "Liberia", "Libia", "Liechtenstein", "Lituania", "Lussemburgo", "Macedonia del Nord", "Madagascar", "Malawi", "Maldive", "Malesia", "Mali", "Malta", "Marocco", "Mauritania", "Mauritius", "Messico", "Micronesia", "Moldavia", "Monaco", "Mongolia", "Montenegro", "Mozambico", "Namibia", "Nauru", "Nepal", "Nicaragua", "Niger", "Nigeria", "Norvegia", "Nuova Zelanda", "Oman", "Paesi Bassi", "Pakistan", "Palau", "Panama", "Papua Nuova Guinea", "Paraguay", "Perù", "Polonia", "Portogallo", "Qatar", "Regno Unito", "Repubblica Ceca", "Repubblica Centrafricana", "Repubblica del Congo", "Repubblica Democratica del Congo", "Repubblica Dominicana", "Romania", "Ruanda", "Russia", "Saint Kitts e Nevis", "Saint Lucia", "Saint Vincent e Grenadine", "Samoa", "San Marino", "Sao Tome e Principe", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Siria", "Slovacchia", "Slovenia", "Somalia", "Spagna", "Sri Lanka", "Stati Uniti", "Sudafrica", "Sudan", "Sudan del Sud", "Suriname", "Svezia", "Svizzera", "Tagikistan", "Tanzania", "Thailandia", "Timor Est", "Togo", "Tonga", "Trinidad e Tobago", "Tunisia", "Turchia", "Turkmenistan", "Tuvalu", "Ucraina", "Uganda", "Ungheria", "Uruguay", "Uzbekistan", "Vanuatu", "Vaticano", "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe"
  ];

  const tipiRichiesta = [
    t.contacts.req1, t.contacts.req2, t.contacts.req3, t.contacts.req4, t.contacts.req5
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.privacy) {
      alert(t.contacts.formPrivacyAlert);
      return;
    }

    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);

      // Reset form
      setFormData({
        azienda: '',
        nome_cognome: '',
        professione: '',
        email: '',
        telefono: '',
        indirizzo: '',
        citta: '',
        cap: '',
        provincia: '',
        nazione: 'Italia',
        tipo_richiesta: '',
        messaggio: '',
        privacy: false
      });

      // Reset success message after 5 seconds
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    }, 2000);
  };

  return (
    <>
      <section className="min-h-screen py-24 md:py-32 bg-[--bg-primary] relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-10 w-72 h-72 bg-[--accent] rounded-full opacity-5 blur-3xl" />
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-purple-500 rounded-full opacity-5 blur-3xl" />
        </div>

        <motion.div
          variants={containerAnimation}
          initial="hidden"
          animate="visible"
          className="max-w-7xl mx-auto px-6 relative z-10"
        >
          {/* Header */}
          <motion.div variants={itemAnimation} className="text-center mb-16">
            <motion.div className="inline-flex items-center justify-center mb-6">
              <div className="w-12 h-px bg-gradient-to-r from-transparent via-[--accent] to-transparent" />
              <span className="text-[--accent] text-sm font-medium tracking-[0.3em] uppercase mx-6">
                07
              </span>
              <div className="w-12 h-px bg-gradient-to-r from-transparent via-[--accent] to-transparent" />
            </motion.div>

            <h2 className="text-4xl md:text-6xl font-display font-bold text-[--text-primary] mb-8 leading-tight">
              {t.contacts.title}
            </h2>
          </motion.div>

          {/* Team Section */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="mb-20"
          >
            <h3 className="text-3xl md:text-4xl font-display font-bold text-[--text-primary] mb-12 text-center">
              {t.contacts.teamTitle.split(' ')[0]} <span className="gradient-text">{t.contacts.teamTitle.split(' ').slice(1).join(' ')}</span>
            </h3>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {teamMembers.map((member, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  whileHover={{ y: -4 }}
                  className="group"
                >
                  <div className="relative bg-[--bg-primary]/40 backdrop-blur-sm rounded-2xl p-8 h-full hover:bg-[--bg-primary]/60 transition-all duration-300 overflow-hidden">
                    {/* Subtle top accent */}
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[--accent]/40 to-transparent"></div>

                    {/* Content wrapper */}
                    <div className="relative z-10 flex flex-col h-full">
                      {/* Name and role */}
                      <div className="text-center mb-6 pb-5 border-b border-[--accent]/10">
                        <h4 className="text-xl font-display font-bold text-[--text-primary] mb-2 leading-tight">
                          {member.name}
                        </h4>
                        <p className="text-[--accent] font-semibold text-sm uppercase tracking-wide">
                          {member.role}
                        </p>
                      </div>

                      {/* Description */}
                      <div className="flex-grow mb-6">
                        <p className="text-[--text-secondary] text-sm leading-relaxed text-center">
                          {member.description}
                        </p>
                      </div>

                      {/* Contact Info */}
                      <div className="space-y-2.5 mt-auto">
                        <div className="flex items-center justify-center text-sm bg-[--bg-secondary]/50 rounded-xl py-2.5 px-4">
                          <Phone className="w-4 h-4 text-[--accent] mr-2.5" />
                          <span className="text-[--text-secondary]">{member.phone}</span>
                        </div>
                        {member.mobile && (
                          <div className="flex items-center justify-center text-sm bg-[--bg-secondary]/50 rounded-xl py-2.5 px-4">
                            <Phone className="w-4 h-4 text-[--accent] mr-2.5" />
                            <span className="text-[--text-secondary]">{t.contacts.mobLabel} {member.mobile}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Company Title */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center justify-center mb-4">
              <div className="w-16 h-px bg-gradient-to-r from-transparent via-[--accent] to-transparent" />
              <h3 className="text-3xl md:text-5xl font-display font-bold text-[--text-primary] mx-6" style={{ fontFamily: "'Bauhaus 93', sans-serif" }}>
                Ellegi S.r.l.
              </h3>
              <div className="w-16 h-px bg-gradient-to-r from-transparent via-[--accent] to-transparent" />
            </div>
          </motion.div>

          {/* Contact Info Section */}
          <motion.div variants={itemAnimation} className="mt-12">
            <div className="glass rounded-3xl p-8">

              <div className="grid md:grid-cols-3 gap-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[--accent] rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[--text-primary] mb-1">
                      {t.contacts.headquarters}
                    </h4>
                    <p className="text-[--text-secondary]">
                      Viale Vittorio Veneto, 6<br />
                      20124 MILANO, Italy
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[--accent] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[--text-primary] mb-1">
                      {t.contacts.phone}
                    </h4>
                    <p className="text-[--text-secondary]">
                      +39 02 2895741
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[--accent] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[--text-primary] mb-1">
                      {t.contacts.email}
                    </h4>
                    <p className="text-[--text-secondary]">
                      comm@lg-mi.it
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Detailed Form */}
          <motion.div variants={itemAnimation} className="mt-16 glass rounded-3xl p-8 md:p-12">
            <div className="flex items-center mb-8">
              <Info className="w-8 h-8 text-[--accent] mr-4" />
              <h3 className="text-2xl md:text-3xl font-display font-semibold text-[--text-primary]">
                {t.contacts.contactUsTitle}
              </h3>
            </div>

            {isSubmitted ? (
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-center py-16"
              >
                <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Send className="w-10 h-10 text-white" />
                </div>
                <h4 className="text-2xl font-semibold text-[--text-primary] mb-4">
                  {t.contacts.successTitle}
                </h4>
                <p className="text-lg text-[--text-secondary] mb-2">
                  {t.contacts.successMessage}
                </p>
                <p className="text-[--text-secondary]">
                  {t.contacts.successFollowUp}
                </p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Azienda */}
                  <div className="form-group">
                    <label className="block text-sm font-medium text-[--text-primary] mb-2">
                      {t.contacts.formCompany} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="azienda"
                      value={formData.azienda}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-[--bg-secondary] border border-gray-300 dark:border-gray-600 rounded-xl text-[--text-primary] placeholder-[--text-secondary] focus:outline-none focus:ring-2 focus:ring-[--accent] focus:border-transparent transition-all"
                      placeholder={t.contacts.companyPlaceholder}
                    />
                  </div>

                  {/* Nome e Cognome */}
                  <div className="form-group">
                    <label className="block text-sm font-medium text-[--text-primary] mb-2">
                      {t.contacts.formName} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="nome_cognome"
                      value={formData.nome_cognome}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-[--bg-secondary] border border-gray-300 dark:border-gray-600 rounded-xl text-[--text-primary] placeholder-[--text-secondary] focus:outline-none focus:ring-2 focus:ring-[--accent] focus:border-transparent transition-all"
                      placeholder={t.contacts.namePlaceholder}
                    />
                  </div>

                  {/* Professione */}
                  <div className="form-group">
                    <label className="block text-sm font-medium text-[--text-primary] mb-2">
                      {t.contacts.formProfession} <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="professione"
                      value={formData.professione}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-[--bg-secondary] border border-gray-300 dark:border-gray-600 rounded-xl text-[--text-primary] focus:outline-none focus:ring-2 focus:ring-[--accent] focus:border-transparent transition-all"
                    >
                      <option value="">{t.contacts.selectProfession}</option>
                      {professioni.map((prof) => (
                        <option key={prof} value={prof}>{prof}</option>
                      ))}
                    </select>
                  </div>

                  {/* Email */}
                  <div className="form-group">
                    <label className="block text-sm font-medium text-[--text-primary] mb-2">
                      {t.contacts.formEmail} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-[--bg-secondary] border border-gray-300 dark:border-gray-600 rounded-xl text-[--text-primary] placeholder-[--text-secondary] focus:outline-none focus:ring-2 focus:ring-[--accent] focus:border-transparent transition-all"
                      placeholder={t.contacts.emailPlaceholder}
                    />
                  </div>

                  {/* Telefono */}
                  <div className="form-group">
                    <label className="block text-sm font-medium text-[--text-primary] mb-2">
                      {t.contacts.formPhone} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-[--bg-secondary] border border-gray-300 dark:border-gray-600 rounded-xl text-[--text-primary] placeholder-[--text-secondary] focus:outline-none focus:ring-2 focus:ring-[--accent] focus:border-transparent transition-all"
                      placeholder={t.contacts.phonePlaceholder}
                    />
                  </div>

                  {/* Indirizzo */}
                  <div className="form-group">
                    <label className="block text-sm font-medium text-[--text-primary] mb-2">
                      {t.contacts.formAddress}
                    </label>
                    <input
                      type="text"
                      name="indirizzo"
                      value={formData.indirizzo}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-[--bg-secondary] border border-gray-300 dark:border-gray-600 rounded-xl text-[--text-primary] placeholder-[--text-secondary] focus:outline-none focus:ring-2 focus:ring-[--accent] focus:border-transparent transition-all"
                      placeholder={t.contacts.addressPlaceholder}
                    />
                  </div>

                  {/* Città */}
                  <div className="form-group">
                    <label className="block text-sm font-medium text-[--text-primary] mb-2">
                      {t.contacts.formCity}
                    </label>
                    <input
                      type="text"
                      name="citta"
                      value={formData.citta}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-[--bg-secondary] border border-gray-300 dark:border-gray-600 rounded-xl text-[--text-primary] placeholder-[--text-secondary] focus:outline-none focus:ring-2 focus:ring-[--accent] focus:border-transparent transition-all"
                      placeholder={t.contacts.cityPlaceholder}
                    />
                  </div>

                  {/* CAP */}
                  <div className="form-group">
                    <label className="block text-sm font-medium text-[--text-primary] mb-2">
                      {t.contacts.formZip}
                    </label>
                    <input
                      type="text"
                      name="cap"
                      value={formData.cap}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-[--bg-secondary] border border-gray-300 dark:border-gray-600 rounded-xl text-[--text-primary] placeholder-[--text-secondary] focus:outline-none focus:ring-2 focus:ring-[--accent] focus:border-transparent transition-all"
                      placeholder={t.contacts.zipPlaceholder}
                    />
                  </div>

                  {/* Provincia */}
                  <div className="form-group">
                    <label className="block text-sm font-medium text-[--text-primary] mb-2">
                      {t.contacts.formProvince}
                    </label>
                    <input
                      type="text"
                      name="provincia"
                      value={formData.provincia}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-[--bg-secondary] border border-gray-300 dark:border-gray-600 rounded-xl text-[--text-primary] placeholder-[--text-secondary] focus:outline-none focus:ring-2 focus:ring-[--accent] focus:border-transparent transition-all"
                      placeholder={t.contacts.provincePlaceholder}
                    />
                  </div>

                  {/* Nazione */}
                  <div className="form-group">
                    <label className="block text-sm font-medium text-[--text-primary] mb-2">
                      {t.contacts.formCountry} <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="nazione"
                      value={formData.nazione}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-[--bg-secondary] border border-gray-300 dark:border-gray-600 rounded-xl text-[--text-primary] focus:outline-none focus:ring-2 focus:ring-[--accent] focus:border-transparent transition-all"
                    >
                      <option value="">{t.contacts.selectCountry}</option>
                      {nazioni.map((nazione) => (
                        <option key={nazione} value={nazione}>{nazione}</option>
                      ))}
                    </select>
                  </div>

                  {/* Tipo di richiesta */}
                  <div className="form-group">
                    <label className="block text-sm font-medium text-[--text-primary] mb-2">
                      {t.contacts.formRequestType} <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="tipo_richiesta"
                      value={formData.tipo_richiesta}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-[--bg-secondary] border border-gray-300 dark:border-gray-600 rounded-xl text-[--text-primary] focus:outline-none focus:ring-2 focus:ring-[--accent] focus:border-transparent transition-all"
                    >
                      <option value="">{t.contacts.selectRequestType}</option>
                      {tipiRichiesta.map((tipo) => (
                        <option key={tipo} value={tipo}>{tipo}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Messaggio */}
                <div className="form-group">
                  <label className="block text-sm font-medium text-[--text-primary] mb-2">
                    {t.contacts.formMessage}
                  </label>
                  <textarea
                    name="messaggio"
                    value={formData.messaggio}
                    onChange={handleInputChange}
                    rows={6}
                    placeholder={t.contacts.messagePlaceholder}
                    className="w-full px-4 py-3 bg-[--bg-secondary] border border-gray-300 dark:border-gray-600 rounded-xl text-[--text-primary] placeholder-[--text-secondary] focus:outline-none focus:ring-2 focus:ring-[--accent] focus:border-transparent transition-all resize-vertical"
                  />
                </div>

                {/* Privacy Policy */}
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="privacy"
                    name="privacy"
                    checked={formData.privacy}
                    onChange={handleInputChange}
                    required
                    className="mt-1 w-4 h-4 text-[--accent] bg-[--bg-secondary] border-gray-300 rounded focus:ring-[--accent] focus:ring-2"
                  />
                  <label htmlFor="privacy" className="text-sm text-[--text-secondary]">
                    <span className="text-red-500">*</span> {t.contacts.formPrivacy}
                  </label>
                </div>

                {/* Submit Button */}
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-[--accent] to-yellow-400 text-white py-4 px-6 rounded-xl font-semibold flex items-center justify-center space-x-2 hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed text-lg"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isSubmitting ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <span>{t.contacts.formSubmit}</span>
                      <Send className="w-5 h-5" />
                    </>
                  )}
                </motion.button>

                <p className="text-sm text-[--text-secondary] text-center">
                  {t.contacts.formRequired}
                </p>
              </form>
            )}
          </motion.div>


        </motion.div>

        {/* Gradient Footer */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[--bg-secondary] via-[--bg-secondary]/60 to-transparent pointer-events-none" />
      </section>

      <Footer
        onNavigate={onNavigate}
        onOpenPrivacyPolicy={onOpenPrivacyPolicy}
        onOpenCookiePolicy={onOpenCookiePolicy}
        onOpenTermsOfService={onOpenTermsOfService}
      />
    </>
  );
}
